import {
  agreementStatusSchema,
  amountSchema,
  bankNameSchema,
  dateSchema,
  FinancingApplication,
  FinancingApplicationSchema,
  financingApplicationStatusSchema,
  financingApplicationTagSchema,
  financingDeclineReasonSchema,
  financingInvoiceSchema,
  financingOfferTermsSchema,
  FinancingPartner,
  idSchema,
  imageSchema,
  imageWithUploadSchema,
  emailSchema,
  nameSchema,
  objectIdSchema,
  phoneSchema,
  orgFinancingProfileSchema,
  paginationWithSearchSchema,
  priceSchema,
  termDurationSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

/* ──────────────────────────── carrier-side ──────────────────────────── */

/**
 * `GET /financing/partners` — legacy alias for the new
 * `GET /lender/active` endpoint, kept so existing carrier-dashboard
 * callers keep compiling during the cutover. Service implementation
 * delegates to `LenderService.getActiveAPI()` and projects the
 * `Lender` doc down to the legacy {@link FinancingPartner} shape.
 *
 * @deprecated Use `LenderGetActiveAPI` from `lender.api.ts` instead.
 */
export const financingGetPartnersAPISchema = z.object({});
export type FinancingGetPartnersAPI = API<
  z.infer<typeof financingGetPartnersAPISchema>,
  { partners: FinancingPartner[] }
>;

/**
 * `POST /financing/uploads/invoice-document` — multipart-or-base64
 * upload of the external invoice document. Server uploads via the
 * existing `image` module under the `financing_invoice_document`
 * folder and returns an `ImageWithUpload` whose `id` / `url` is
 * then stamped onto `invoice.document` of the subsequent application
 * `POST`.
 *
 * Financing profile reads / writes live on the shared
 * `GET /org/profile` and `PATCH /org/profile` endpoints — see
 * `libs/shared/api/src/api/org-profile.api.ts`. The MVP cleanup
 * removed the dedicated `PATCH/GET /financing/profile` routes so
 * `financialStatements[]` flow through the same `imageWithUpload`
 * pipeline as `legal.*Image` and `bank.accountImage`.
 */
export const financingUploadInvoiceDocumentAPISchema = z.object({
  image: imageWithUploadSchema,
  /** Pre-supplied client name; flows through to metadata for audit. */
  clientName: z.string().trim().optional(),
});
export type FinancingUploadInvoiceDocumentAPI = API<
  z.infer<typeof financingUploadInvoiceDocumentAPISchema>,
  z.infer<typeof imageWithUploadSchema>
>;

/**
 * `POST /financing/applications` — primary write path. Body carries
 * the invoice block (Hero ref or external upload), requested terms,
 * and optional lender selection.
 *
 * The financing profile is NOT accepted on this payload — the carrier
 * saves it via `PATCH /org/profile` before submitting. The service
 * snapshots `Org.financingProfile` into `profileSnapshot`; if missing,
 * the request is rejected with `financing_profile_required`.
 */
export const financingCreateApplicationAPISchema = z.object({
  invoice: financingInvoiceSchema.describe(
    'Invoice block. Either references a Hero `Invoice._id` (source=hero) or carries the external invoice metadata + uploaded document (source=external).',
  ),
  requestedAmount: priceSchema.describe(
    'How much the carrier wants advanced. Lender quotes are submitted on top of this number; the carrier picks one.',
  ),
  requestedTerm: termDurationSchema.describe(
    "Requested repayment term (e.g. '30_days', '60_days'). Lender may counter with a different term.",
  ),
  lenderIds: z
    .array(objectIdSchema)
    .max(10)
    .optional()
    .describe(
      'Optional lender allow-list (`Lender._id[]`). Omit / empty array → fan out to every active lender on file (Hero is a network, never picks lenders for the carrier). Cap = 10.',
    ),
});
export type FinancingCreateApplicationAPI = API<
  z.infer<typeof financingCreateApplicationAPISchema>,
  FinancingApplication
>;

/**
 * `GET /financing/applications` — list of the caller's applications.
 * Visibility is enforced via `tenants[]` $in against the current
 * user's identity entries (`org_id` first, then `user_id`).
 */
export const financingGetApplicationsAPISchema = z
  .object({
    status: financingApplicationStatusSchema
      .optional()
      .describe(
        "Filter by `FinancingApplication.status` (e.g. 'pending_review' / 'submitted' / 'accepted' / 'funded' / 'repaid' / 'cancelled').",
      ),
    invoiceId: objectIdSchema
      .optional()
      .describe(
        'Filter to applications backing a specific Invoice (`FinancingApplication.invoice._id`).',
      ),
  })
  .extend(paginationWithSearchSchema.shape);
export type FinancingGetApplicationsAPI = API<
  z.infer<typeof financingGetApplicationsAPISchema>,
  FinancingApplication[]
>;

/**
 * `POST /financing/applications/:id/accept-offer` — carrier picks
 * one of the `offered` rows. Service flips that row's `decision`
 * to `'accepted'` and bumps `status` to `'accepted'`. Idempotent
 * on the same `(application, offer)` pair. Admin-callable too
 * (acting on behalf of the carrier).
 */
export const financingAcceptOfferAPISchema = z.object({
  _id: objectIdSchema.describe('`FinancingApplication._id` carrying the offer to accept.'),
  offerId: z
    .string()
    .trim()
    .min(1)
    .describe(
      "Per-application offer id (`FinancingApplication.offers[i].offerId`) — the carrier's pick from the lender quotes.",
    ),
});
export type FinancingAcceptOfferAPI = API<
  z.infer<typeof financingAcceptOfferAPISchema>,
  FinancingApplication
>;

/**
 * Carrier-visible lender contact row — omits internal flags such as
 * `receiveNewApplicationEvent`.
 */
export const financingOfferLenderContactViewSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema.optional(),
});

/**
 * `GET /financing/applications/offer-lender-contacts` — returns the
 * opted-in contacts for the lender on a specific offer row. Only
 * contacts with `receiveNewApplicationEvent === true` are included
 * (same pool Hero emails when routing a new application).
 *
 * Scoped to the caller's `tenants[]` entry on the application so
 * carriers cannot enumerate contacts for applications they do not
 * own.
 */
export const financingGetOfferLenderContactsAPISchema = z.object({
  _id: objectIdSchema,
  offerId: idSchema,
});
export type FinancingGetOfferLenderContactsAPI = API<
  z.infer<typeof financingGetOfferLenderContactsAPISchema>,
  {
    lenderId: string;
    lenderName: string;
    contacts: z.infer<typeof financingOfferLenderContactViewSchema>[];
  }
>;

/**
 * `POST /financing/applications/submit-repayment-receipt` — carrier
 * uploads proof they've repaid the lender. The service stores the
 * image via `imageService.financingRepaymentReceipt(...)`, stamps
 * `FinancingApplication.repaymentReceipt`, and transitions the
 * application to `awaiting_repayment_approval` so a Hero admin can
 * review.
 *
 * Caller must be a tenant on the application; receipts can only
 * be submitted while the application is in `funded` status (the
 * upstream funded-once → repaid invariant). Re-uploads while
 * already in `awaiting_repayment_approval` replace the previous
 * receipt — the admin only sees the latest version.
 */
export const financingSubmitRepaymentReceiptAPISchema = z.object({
  _id: objectIdSchema,
  image: imageWithUploadSchema,
  notes: z.string().trim().optional(),
  /**
   * Carrier-declared wire date. Optional — the SPA gates on it
   * being present at the UI layer (the Mark-as-paid modal requires
   * a date pick) but the backend stays permissive so a quick admin
   * back-fill via the API client doesn't trip on a missing field.
   * Stored under `repaymentReceipt.repaidAt` and presented to the
   * reviewer next to the server-stamped `submittedAt` so back-
   * dating is auditable.
   */
  at: dateSchema.optional(),
});
export type FinancingSubmitRepaymentReceiptAPI = API<
  z.infer<typeof financingSubmitRepaymentReceiptAPISchema>,
  FinancingApplication
>;

/**
 * `POST /financing/applications/approve-repayment` — super-admin
 * action that flips `awaiting_repayment_approval → repaid` and
 * stamps `outcome.repaidAt`. The carrier dashboard reflects the
 * new status on its next refetch.
 *
 * The approval reuses the carrier's already-uploaded
 * `repaymentReceipt.image` as `outcome.repaymentProof` — there is
 * no separate proof re-upload. The body just lets the reviewer
 * confirm / correct the date the wire actually cleared.
 *
 *  - **`at`** — optional confirmed repaid date. Falls back to
 *    `repaymentReceipt.repaidAt` (the carrier's claim), then to
 *    server `now()` when neither is on file. Stored as the
 *    canonical `outcome.repaidAt`.
 *
 * Rejecting a receipt is its own action (see
 * {@link FinancingRejectRepaymentAPI}) so the admin can leave the
 * application in `funded` with the receipt cleared, letting the
 * carrier retry without re-opening anything.
 */
export const financingApproveRepaymentAPISchema = z.object({
  _id: objectIdSchema,
  at: dateSchema.optional(),
});
export type FinancingApproveRepaymentAPI = API<
  z.infer<typeof financingApproveRepaymentAPISchema>,
  FinancingApplication
>;

/**
 * `POST /financing/applications/reject-repayment` — super-admin
 * action that reverts `awaiting_repayment_approval → funded`,
 * clears `repaymentReceipt`, and records the rejection reason in
 * `outcome.notes`. The carrier can then submit a new receipt.
 */
export const financingRejectRepaymentAPISchema = z.object({
  _id: objectIdSchema,
  notes: z.string().trim().optional(),
});
export type FinancingRejectRepaymentAPI = API<
  z.infer<typeof financingRejectRepaymentAPISchema>,
  FinancingApplication
>;

/**
 * `PATCH /financing/applications/:id/tags` — replace the full
 * `tags[]` array on an application. The pilot UI only needs
 * set-the-list semantics (super-admin curates the markers as a
 * triage exercise); deltas can wait for v0.1 when we add a sweep
 * cron that owns `late` / `overdue` automatically.
 *
 * Super-admin only — there's no per-tenant write surface for these
 * markers in the pilot.
 */
export const financingUpdateTagsAPISchema = z.object({
  _id: objectIdSchema,
  tags: z.array(financingApplicationTagSchema),
});
export type FinancingUpdateTagsAPI = API<
  z.infer<typeof financingUpdateTagsAPISchema>,
  FinancingApplication
>;

/* ────────────────────────── public partner side ───────────────────────── */

/**
 * Per-partner application view returned by
 * `GET /financing/partner?_id=<appId>&p=<partnerId>` (frontend URL is
 * `/financing/partner/:appId?p=<partnerId>`).
 *
 * Contact-masking rule: applicant phone/email, contact party details,
 * and full org refs are returned ONLY when `interestedAt` is set on
 * the partner's own offer. Before that the partner sees only the
 * carrier company name and the depersonalized invoice/profile snapshot
 * fields needed to underwrite.
 *
 * @deprecated Use {@link FinancingLenderViewAPI} (`GET /financing/lender-view-application`).
 * This shape is preserved one release for old `/financing/partner` links
 * in lender inboxes that haven't been re-clicked yet.
 */
export const financingPartnerApplicationViewSchema = z.object({
  applicationId: objectIdSchema,
  /** Stable per-offer id for the partner's own `offers[]` entry. */
  offerId: z.string().trim().min(1),
  /** Whether contact info has been revealed (mirrors `myOffer.interestedAt !== undefined`). */
  contactRevealed: z.boolean(),
  ref: z.string().trim(),
  carrier: z.object({
    /** Always returned — carrier company display name. */
    name: z.string(),
    monthsActive: z.number().int().nonnegative(),
    /** Only set when `contactRevealed`. */
    contactName: z.string().optional(),
    contactPhone: z.string().optional(),
    contactEmail: z.string().optional(),
  }),
  requested: z.object({
    amount: priceSchema,
    term: termDurationSchema,
  }),
  invoice: z.object({
    source: z.enum(['hero', 'external']),
    amount: priceSchema,
    currency: z.string(),
    expectedDueDate: z.date(),
    clientName: z.string(),
    documentUrl: z.string().optional(),
    invoiceNumber: z.string().optional(),
  }),
  profileSnapshot: orgFinancingProfileSchema,
  /** The partner's own offer entry (always present — the GET cannot succeed without it). */
  myOffer: z.object({
    /**
     * Lifecycle state of the lender's offer row. The lender writes
     * `pending → interested → offered | declined`; `accepted` is
     * set by the carrier (or super-admin) and surfaces here so the
     * lender sees "carrier picked you" in the read-only state.
     */
    decision: z.enum([
      'pending',
      'interested',
      'offered',
      'declined',
      'accepted',
    ]),
    interestedAt: z.date().optional(),
    decidedAt: z.date().optional(),
    terms: financingOfferTermsSchema.optional(),
    declineReason: financingDeclineReasonSchema.optional(),
    declineNotes: z.string().optional(),
    isCounterOffer: z.boolean().optional(),
  }),
});
export type FinancingPartnerApplicationView = z.infer<
  typeof financingPartnerApplicationViewSchema
>;

/**
 * `GET /financing/partner` — public, lender-id-protected. The
 * `p` query carries the hex `Lender._id` of the partner the link
 * was issued to; the service matches it against
 * `offers[i].lenderId`. No auth token is required for the pilot —
 * see `docs/financing/partner-access.md`.
 */
export const financingPartnerGetAPISchema = z.object({
  _id: objectIdSchema,
  p: z.string().trim().min(1),
});
export type FinancingPartnerGetAPI = API<
  z.infer<typeof financingPartnerGetAPISchema>,
  FinancingPartnerApplicationView
>;

/**
 * `POST /financing/partner/express-interest` — public,
 * partner-id-protected. Stamps `offers[i].interestedAt`, unlocking
 * the contact-info reveal on subsequent `GET`s.
 */
export const financingPartnerExpressInterestAPISchema = z.object({
  _id: objectIdSchema,
  p: z.string().trim().min(1),
});
export type FinancingPartnerExpressInterestAPI = API<
  z.infer<typeof financingPartnerExpressInterestAPISchema>,
  FinancingPartnerApplicationView
>;

/**
 * `POST /financing/partner/offer` — public, partner-id-protected.
 * Submits or updates the partner's offer entry. Service rejects with
 * `financing_interest_not_expressed` if the partner hasn't clicked
 * "I'm interested" first.
 */
export const financingPartnerSubmitOfferAPISchema = z
  .object({
    _id: objectIdSchema,
    p: z.string().trim().min(1),
    decision: z.enum(['offered', 'declined']),
    terms: financingOfferTermsSchema.optional(),
    declineReason: financingDeclineReasonSchema.optional(),
    declineNotes: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.decision === 'offered' && !data.terms) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms'],
        message: 'terms is required when decision is "offered"',
      });
    }
    if (data.decision === 'declined' && !data.declineReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['declineReason'],
        message: 'declineReason is required when decision is "declined"',
      });
    }
  });
export type FinancingPartnerSubmitOfferAPI = API<
  z.infer<typeof financingPartnerSubmitOfferAPISchema>,
  FinancingPartnerApplicationView
>;

/* ────────────────────── public lender-view (single call) ────────────────────── */

/**
 * Single-call payload returned by `GET /financing/lender-view-application`.
 *
 * Replaces the trio of legacy `partner/*` reads with one consolidated
 * response. Carries everything the public lender page needs to render
 * fully on first load: the application, the carrier org, the invoice
 * payer client (external invoices only), the bilateral agreement
 * (external invoices only), and the viewing lender's display info.
 *
 * Why one shape?
 *
 *  - **Single source of truth.** The page fires ONE network request
 *    instead of three; TanStack Query persists the result via
 *    `PersistQueryClientProvider` so a hard refresh re-renders
 *    instantly with the cached `lender.name` / `lender.logoUrl`.
 *  - **Auth-ready.** When v1 promotes lenders to authed `Org`s, the
 *    endpoint stays put — only the access check flips from
 *    `?i=<lenderId>` string-match to `Org._id === viewer._id`. The
 *    response shape doesn't change.
 *  - **Mask-aware.** `contactRevealed` is computed from
 *    `offer.interestedAt`. Contact fields on `org.contact` and
 *    `client.contact` are stripped server-side until interest is
 *    expressed; the boolean lets the page render the masked variant
 *    with no client-side guessing.
 */
export const financingLenderViewSchema = z.object({
  /** Whether contact info has been revealed (mirrors `offer.interestedAt !== undefined`). */
  contactRevealed: z.boolean(),
  application: z.object({
    _id: objectIdSchema,
    /** Human-readable ref (`FIN-2026-00042`). */
    ref: z.string().trim(),
    status: financingApplicationStatusSchema,
    requested: z.object({
      amount: priceSchema,
      term: termDurationSchema,
    }),
    invoice: z.object({
      source: z.enum(['hero', 'external']),
      amount: priceSchema,
      currency: z.string(),
      expectedDueDate: z.date(),
      clientName: z.string(),
      documentUrl: z.string().optional(),
      invoiceNumber: z.string().optional(),
    }),
    profileSnapshot: orgFinancingProfileSchema,
    /**
     * The viewing lender's own offer row. Always present — the
     * endpoint cannot succeed without resolving an offer for the
     * `(appId, lenderId)` pair.
     */
    myOffer: z.object({
      offerId: z.string().trim().min(1),
      /**
       * Lifecycle state of the lender's offer row. The lender
       * writes `pending → interested → offered | declined`;
       * `accepted` is set by the carrier (or super-admin) and
       * surfaces here so the lender sees "carrier picked you" in
       * the read-only state.
       */
      decision: z.enum([
        'pending',
        'interested',
        'offered',
        'declined',
        'accepted',
      ]),
      interestedAt: z.date().optional(),
      decidedAt: z.date().optional(),
      terms: financingOfferTermsSchema.optional(),
      declineReason: financingDeclineReasonSchema.optional(),
      declineNotes: z.string().optional(),
      isCounterOffer: z.boolean().optional(),
    }),
    /**
     * Outcome timestamps surfaced to the lender — only revealed
     * once their offer has been chosen (i.e.
     * `myOffer.decision === 'accepted'`). The backend strips the
     * whole shape for any other decision so a competing lender's
     * funding date never leaks across the wall.
     *
     * Fields are progressively filled by the lender:
     *  - `fundedAt`   — set by {@link FinancingLenderMarkFundedAPI}.
     *  - `repaidAt`   — set by {@link FinancingLenderMarkRepaidAPI}.
     *
     * The lender-view UI uses these to render the read-only
     * "funded on X / repaid on Y" banners and to seed the repayment
     * panel's "funded on" context row.
     */
    outcome: z
      .object({
        fundedAt: z.date().optional(),
        repaidAt: z.date().optional(),
      })
      .optional(),
    /**
     * Carrier-uploaded repayment receipt — projected ONLY when the
     * viewing lender is the accepted one and the application is in
     * `awaiting_repayment_approval` (review needed) or `repaid`
     * (post-approval reference). The lender-view "review" mode
     * renders this as the carrier's submission: image link + notes
     * + claimed `repaidAt` next to a server-stamped `submittedAt`.
     *
     * `imageUrl` is the flat Cloudinary URL of the uploaded proof;
     * we deliberately drop the `imageId` because the lender only
     * needs the click-through. The `imageId` stays on the receipt
     * document for any future internal reference.
     */
    repaymentReceipt: z
      .object({
        imageUrl: z.string().optional(),
        notes: z.string().optional(),
        repaidAt: z.date().optional(),
        submittedAt: z.date(),
      })
      .optional(),
    /**
     * Last lender / admin rejection notes carried forward on
     * `outcome.notes`. When the lender (or admin) rejects a
     * carrier-submitted receipt, the status reverts to `'funded'`
     * and the reason lives on `outcome.notes` until the carrier
     * uploads a new receipt. Surfaced here so the lender-view UI
     * can show "your previous review reason: …" when the same
     * lender re-enters the page after a bounce. Cleared the moment
     * a fresh `repaymentReceipt` lands.
     */
    lastRejectionNotes: z.string().optional(),
    /**
     * Runner-up signal — `true` iff some OTHER lender's offer on
     * this application carries `decision: 'accepted'`. Always
     * `false` (or omitted) for the winning lender themselves; the
     * winner reads `myOffer.decision === 'accepted'` instead.
     *
     * Drives the lender-view "another lender was picked" info
     * banner that replaces the green "we received your offer" /
     * "I'm interested" panels once the application is off the
     * market. We deliberately surface a flat boolean (no winner
     * name, no terms) so a runner-up never sees who beat them or
     * with what numbers — leaks both the competing lender's
     * relationship and pricing.
     */
    acceptedByOtherLender: z.boolean().optional(),
  }),
  org: z.object({
    _id: objectIdSchema,
    name: z.string(),
    /** Months since the org's first activity — coarse tenure proxy. */
    monthsActive: z.number().int().nonnegative(),
    businessType: z.string().optional(),
    establishedYear: z.number().int().optional(),
    /**
     * Self-declared legal-entity fields surfaced to the lender so
     * they can verify the carrier's incorporation paperwork before
     * funding. All optional — a carrier may legitimately be
     * mid-onboarding and not have uploaded the CR yet, in which case
     * the lender-view UI degrades gracefully and shows only what's
     * present. Document URLs (`crImageUrl`, `taxImageUrl`) are flat
     * strings (not `image`) because the lender only needs the click-
     * through link, not the imageId.
     */
    legalName: z.string().optional(),
    crNumber: z.string().optional(),
    crImageUrl: z.string().optional(),
    taxNumber: z.string().optional(),
    taxImageUrl: z.string().optional(),
    /** Present iff `contactRevealed`. */
    contact: z
      .object({
        name: z.string().optional(),
        phone: z.string().optional(),
        email: z.string().optional(),
      })
      .optional(),
  }),
  /**
   * Invoice payer — external invoices only (Hero invoices already
   * carry a registered client we trust for the pilot). Contact info
   * masked until interest is expressed.
   */
  client: z
    .object({
      _id: objectIdSchema,
      name: z.string(),
      contact: z
        .object({
          name: z.string().optional(),
          phone: z.string().optional(),
          email: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  /**
   * Bilateral agreement between the carrier and the invoice payer —
   * external invoices only. The lender uses this to confirm the
   * counterparty has a signed contract on file.
   */
  agreement: z
    .object({
      _id: objectIdSchema,
      status: agreementStatusSchema,
      contractPDF: imageSchema.optional(),
    })
    .optional(),
  /**
   * Display info for the viewing lender. Used by the public page
   * hero (name, logo, brand colour) and cached locally so the
   * Tarmeez-style branding sticks across refreshes.
   */
  lender: z.object({
    _id: objectIdSchema,
    name: nameSchema,
    tagline: z.string().optional(),
    logoUrl: z.string().optional(),
    brandColor: z.string().optional(),
    website: z.string().optional(),
  }),
  /**
   * Carrier's primary bank account — only present when the viewing
   * lender's offer has `decision: 'accepted'`. Used by the
   * lender-side "Record funding" panel so the lender knows where to
   * wire the advance. The `accountImage` (IBAN screenshot) is
   * intentionally omitted — the lender needs the routing fields, not
   * the audit-trail screenshot.
   *
   * Carriers without a saved bank yield `carrierBank: undefined`;
   * the panel renders a "no bank on file" empty state in that case.
   */
  carrierBank: z
    .object({
      bankName: bankNameSchema,
      accountName: z.string(),
      IBAN: z.string(),
    })
    .optional(),
});
export type FinancingLenderView = z.infer<typeof financingLenderViewSchema>;

/**
 * `GET /financing/lender-view-application` — single public read for
 * the lender-view page. `i` carries the hex `Lender._id` of the
 * partner the link was issued to.
 *
 * Returns `financing_partner_invite_not_found` (single 404 surface)
 * for any unknown `(appId, lenderId)` pair so a guessed `?i=` value
 * cannot probe for application or lender existence.
 */
export const financingLenderViewAPISchema = z.object({
  _id: objectIdSchema,
  i: z.string().trim().min(1),
});
export type FinancingLenderViewAPI = API<
  z.infer<typeof financingLenderViewAPISchema>,
  FinancingLenderView
>;

/**
 * `POST /financing/lender-view-application/express-interest` —
 * lender clicks "I'm interested". Stamps `offer.interestedAt` and
 * returns the same {@link FinancingLenderView} shape (now with
 * `contactRevealed: true` and contact fields filled in) so the
 * page hydrates from a single response.
 */
export const financingLenderExpressInterestAPISchema = z.object({
  _id: objectIdSchema,
  i: z.string().trim().min(1),
});
export type FinancingLenderExpressInterestAPI = API<
  z.infer<typeof financingLenderExpressInterestAPISchema>,
  FinancingLenderView
>;

/**
 * `POST /financing/lender-view-application/offer` — lender submits
 * or updates an offer. Mirrors the legacy partner-side rules:
 * `terms` required when `decision === 'offered'`, `declineReason`
 * required when `decision === 'declined'`. Service rejects with
 * `financing_interest_not_expressed` if the lender hasn't clicked
 * "I'm interested" first.
 */
export const financingLenderSubmitOfferAPISchema = z
  .object({
    _id: objectIdSchema,
    i: z.string().trim().min(1),
    decision: z.enum(['offered', 'declined']),
    terms: financingOfferTermsSchema.optional(),
    declineReason: financingDeclineReasonSchema.optional(),
    declineNotes: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.decision === 'offered' && !data.terms) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms'],
        message: 'terms is required when decision is "offered"',
      });
    }
    if (data.decision === 'declined' && !data.declineReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['declineReason'],
        message: 'declineReason is required when decision is "declined"',
      });
    }
  });
export type FinancingLenderSubmitOfferAPI = API<
  z.infer<typeof financingLenderSubmitOfferAPISchema>,
  FinancingLenderView
>;

/**
 * `POST /financing/lender-view-application/mark-funded` — lender
 * stamps the operational funding event from the public lender-view
 * page once the carrier has accepted their offer.
 *
 * Service invariants (enforced; mirrors the admin-side
 * {@link financingMarkOutcomeAPISchema} for the `funded` kind):
 *  - The lender's own offer (matched by `?i=<lenderId>`) must have
 *    `decision === 'accepted'`. The lender cannot fund an offer the
 *    carrier didn't pick.
 *  - The application must be in `status: 'accepted'`. Funding twice
 *    is rejected with `financing_already_funded`.
 *
 * Body shape:
 *  - **`at`** — optional ISO timestamp. Lets the lender backdate
 *    the write to match the actual wire date. Falls back to server
 *    `now()` when omitted. Same field semantics as the admin
 *    payload's `at`.
 *  - **`fundingProof`** — optional `imageWithUploadSchema`. Routed
 *    through `ImageService.financingFundingProof` before persist,
 *    same as the admin write.
 *  - **`notes`** — optional free-text comment, surfaced on the
 *    `outcome_funded` event payload.
 *
 * Returns the refreshed {@link FinancingLenderView} so the SPA
 * hydrates the post-funding UI from a single response.
 */
export const financingLenderMarkFundedAPISchema = z.object({
  _id: objectIdSchema,
  i: z.string().trim().min(1),
  at: dateSchema.optional(),
  fundingProof: imageWithUploadSchema.optional(),
  notes: z.string().trim().optional(),
});
export type FinancingLenderMarkFundedAPI = API<
  z.infer<typeof financingLenderMarkFundedAPISchema>,
  FinancingLenderView
>;

/**
 * `POST /financing/lender-view-application/mark-repaid` — lender
 * confirms the carrier paid them back (advance + fee). Closes out
 * the loop opened by {@link financingLenderMarkFundedAPISchema}
 * once the funds have landed in the lender's account.
 *
 * Two valid entry paths, both end with `status: 'repaid'`:
 *
 *  1. **Approval path** — `status: 'awaiting_repayment_approval'`.
 *     The carrier has uploaded a {@link FinancingRepaymentReceipt}
 *     and is waiting for review. The lender reviews the carrier's
 *     image / notes / claimed `repaidAt`, optionally corrects the
 *     date, and approves. The carrier's
 *     `repaymentReceipt.image` is reused as the canonical
 *     `outcome.repaymentProof` — no re-upload required.
 *     `repaymentProof` in the body is optional on this path; if
 *     provided it overrides the carrier's image.
 *
 *  2. **Direct-record path** — `status: 'funded'`. The carrier
 *     never submitted a receipt but the lender already got the
 *     money off-platform. The lender records the repayment with
 *     their own proof — `repaymentProof` is required at the SPA
 *     layer for this path.
 *
 * Common invariants enforced server-side:
 *  - The lender's own offer (matched by `?i=<lenderId>`) must
 *    have `decision === 'accepted'`. The lender cannot stamp
 *    repayment on an offer the carrier didn't pick.
 *  - Status must be either `funded` or
 *    `awaiting_repayment_approval`; any other status returns
 *    `financing_not_funded_yet` / `financing_already_repaid`.
 *
 * Body shape (no `offerId` — the accepted offer is implicit):
 *  - **`at`** — optional ISO timestamp; defaults to the carrier's
 *    claimed `receipt.repaidAt`, then server `now()`.
 *  - **`repaymentProof`** — optional. See path notes above.
 *  - **`notes`** — optional free-text comment, surfaced on the
 *    `outcome_repaid` event payload.
 *
 * Returns the refreshed {@link FinancingLenderView}.
 */
export const financingLenderMarkRepaidAPISchema = z.object({
  _id: objectIdSchema,
  i: z.string().trim().min(1),
  at: dateSchema.optional(),
  repaymentProof: imageWithUploadSchema.optional(),
  notes: z.string().trim().optional(),
});
export type FinancingLenderMarkRepaidAPI = API<
  z.infer<typeof financingLenderMarkRepaidAPISchema>,
  FinancingLenderView
>;

/**
 * `POST /financing/lender-view-application/reject-repayment` —
 * lender bounces back the carrier's submitted receipt (looks
 * forged, wrong amount, wrong account, etc.). Mirrors the admin
 * {@link FinancingRejectRepaymentAPI} surface but is keyed by
 * `(_id, i)` and gated on the lender's own offer being the
 * accepted one. The eventual goal is for the lender to handle
 * approve/reject without admin involvement; the admin path stays
 * as a fallback for support escalations.
 *
 * Service invariants:
 *  - `offer.decision === 'accepted'` for the lender behind `?i=`.
 *  - `application.status === 'awaiting_repayment_approval'`.
 *
 * Side effects (mirror admin reject):
 *  - Status reverts to `'funded'`, `repaymentReceipt` is cleared
 *    so the carrier can re-submit with a corrected upload.
 *  - `outcome.notes` is overwritten with the rejection reason so
 *    the carrier sees why it bounced when they re-open the
 *    Mark-as-paid drawer.
 *  - Appends a `repayment_rejected` lifecycle event.
 *
 *  - **`notes`** — REQUIRED. The reason gets surfaced to the
 *    carrier so they know what to fix. The SPA enforces a
 *    non-empty trim at the dialog layer.
 */
export const financingLenderRejectRepaymentAPISchema = z.object({
  _id: objectIdSchema,
  i: z.string().trim().min(1),
  notes: z.string().trim().min(1),
});
export type FinancingLenderRejectRepaymentAPI = API<
  z.infer<typeof financingLenderRejectRepaymentAPISchema>,
  FinancingLenderView
>;

/* ──────────────────────────── admin-side ──────────────────────────── */

/**
 * Outcome states the admin can write. Maps 1:1 to the matching
 * `FinancingApplicationStatus` value the service stamps along with
 * `outcome.fundedAt` / `repaidAt` / `defaultedAt`.
 */
export const financingOutcomeKindSchema = z.enum(['funded', 'repaid', 'defaulted']);
export type FinancingOutcomeKind = z.infer<typeof financingOutcomeKindSchema>;

/**
 * `POST /financing/applications/:id/outcome` — admin updates the
 * lifecycle outcome.
 *
 * The body is a superset of every kind's fields — only the slice
 * matching `kind` is read, the rest is ignored. This lets the
 * admin UI ship one payload shape across all three outcomes
 * (`funded` / `repaid` / `defaulted`) without per-kind routes.
 *
 * Important: terms (advance / fee / fee% / repayment date) are
 * NOT collected here. They live on the accepted offer's
 * {@link financingOfferTermsSchema} and were locked when the
 * carrier (or admin acting on the carrier's behalf) accepted that
 * offer. The funded payload only records the operational facts —
 * date + proof + notes.
 *
 * Field semantics:
 *  - **`at`** — optional admin-supplied timestamp. Lets ops
 *    backdate a write to match the actual disbursement / repayment
 *    date. Falls back to `now()` server-side.
 *  - **`offerId`** (`kind: 'funded'` only) — which offer is being
 *    funded. Required when no offer is yet accepted (soft gate);
 *    the service flips that row's `decision` to `'accepted'` and
 *    stamps an `offer_accepted` event before stamping funded.
 *    Optional when an offer already has `decision: 'accepted'`
 *    (the payload `offerId`, if present, must match that row).
 *  - **`*Proof` images** — uploaded through the shared
 *    `imageWithUploadSchema` pipeline: `{ upload: base64 }` for a
 *    new push, `{ id, url }` for an already-uploaded asset. The
 *    service routes these through `ImageService.financingFundingProof`
 *    / `financingRepaymentProof`.
 *  - **All non-`kind` fields are optional.** A bare body of
 *    `{ _id, kind: 'funded', offerId }` is valid.
 */
export const financingMarkOutcomeAPISchema = z.object({
  _id: objectIdSchema,
  kind: financingOutcomeKindSchema,
  at: dateSchema.optional(),

  // funded
  offerId: idSchema.optional(),
  fundingProof: imageWithUploadSchema.optional(),

  // repaid
  repaymentProof: imageWithUploadSchema.optional(),

  // defaulted
  defaultedAmount: priceSchema.optional(),

  notes: z.string().trim().optional(),
});
export type FinancingMarkOutcomeAPI = API<
  z.infer<typeof financingMarkOutcomeAPISchema>,
  FinancingApplication
>;

/**
 * `POST /financing/applications/:id/send-to-lenders` — super-admin
 * gate that fans lender invites out. Works for two flows:
 *
 *  - **First send** (status `pending_review`): flips to `submitted`
 *    and writes `offers[]` for the resolved lender list.
 *  - **Re-send** (status `submitted`): appends offers for any
 *    lenders not already invited. Status stays `submitted`.
 *
 * Lender resolution:
 *  - `selectAll: true` (or `lenderIds` omitted) → invite every
 *    active lender on file. Matches the "network not broker" intent
 *    — the platform never picks for the carrier.
 *  - `lenderIds: [...]` → resolve those specific lenders (each must
 *    be `status: 'active'`; cap is
 *    `APP_CONFIG.financing.maxLendersPerApplication`).
 *
 * The service rejects with `financing_application_closed` once the
 * application has moved past `submitted` (no more invites once an
 * offer is accepted or funded).
 */
export const financingSendToLendersAPISchema = z.object({
  _id: objectIdSchema,
  /**
   * Explicit lender allow-list. Mutually exclusive with `selectAll`
   * — if both are present, `lenderIds` wins. Empty array is
   * normalised to "all active" for back-compat with the legacy v0
   * callers.
   */
  lenderIds: z.array(objectIdSchema).max(20).optional(),
  /**
   * Shortcut for "every active lender on file". When `true` the
   * service ignores `lenderIds`. When `false` (or omitted) the
   * service uses `lenderIds`; if both are unset it still falls
   * back to all-active for back-compat.
   */
  selectAll: z.boolean().optional(),
});
export type FinancingSendToLendersAPI = API<
  z.infer<typeof financingSendToLendersAPISchema>,
  FinancingApplication
>;

/**
 * `POST /financing/applications/:id/cancel` — super-admin cancels a
 * `pending_review` or `submitted` application. Records a
 * `cancelled_by_admin` event and stamps `status: 'cancelled'`.
 */
export const financingCancelAPISchema = z.object({
  _id: objectIdSchema,
  notes: z.string().trim().optional(),
});
export type FinancingCancelAPI = API<
  z.infer<typeof financingCancelAPISchema>,
  FinancingApplication
>;

/**
 * `PATCH /financing/applications/invoice` — super-admin patches
 * editable invoice fields on a financing application. Used by the
 * admin drawer when ops needs to correct a typo (wrong amount,
 * wrong invoice number) or swap the uploaded PDF for an external
 * invoice.
 *
 * Only the listed fields are accepted: the `source` discriminator
 * and the linked `_id` / `clientId` are immutable post-submit.
 *
 * - `document` accepts an `imageWithUploadSchema` (so the operator
 *   can re-upload a corrected PDF inline) — the service routes it
 *   through `ImageService.financingInvoiceDocument` before persist.
 */
export const financingAdminUpdateInvoiceAPISchema = z.object({
  _id: objectIdSchema,
  amount: amountSchema.optional(),
  expectedDueDate: dateSchema.optional(),
  invoiceNumber: z.string().trim().optional(),
  clientName: nameSchema.optional(),
  document: imageWithUploadSchema.optional(),
});
export type FinancingAdminUpdateInvoiceAPI = API<
  z.infer<typeof financingAdminUpdateInvoiceAPISchema>,
  FinancingApplication
>;

/**
 * `POST /financing/applications/:id/admin/offer` — super-admin
 * back-fills an offer for the out-of-band case where a lender
 * skipped our partner-view UI and emailed Hero with terms (or
 * declined out-of-band). Two modes:
 *
 *  - **Append** (`offerId` omitted): pushes a new offer for the
 *    given `lenderId`. Lender must exist and be `status: 'active'`.
 *    Forbidden when an offer already exists for that lender on this
 *    application — use the edit mode instead.
 *  - **Edit** (`offerId` present): patches the matching offer
 *    in-place. Lender is immutable on an existing offer; the
 *    operator changes decision / terms / decline detail.
 *
 * Validation mirrors `lenderSubmitOfferAPISchema`:
 *  - `terms` required when `decision === 'offered'`.
 *  - `declineReason` required when `decision === 'declined'`.
 *  - `decision: 'pending'` is accepted (useful to seed an entry
 *    before the lender actually responds, though the common case
 *    is `'offered'`).
 *  - **`decision: 'accepted'` is NOT writable here.** The admin
 *    upsert endpoint stands in for the lender; carrier acceptance
 *    goes through `acceptOfferAPI` (or `markOutcomeAPI` soft-
 *    accept on the funded transition).
 *
 * The service stamps an `offer_upserted_by_admin` event with the
 * actor's `isSuperAdmin: true` so the audit log distinguishes ops
 * back-fills from real lender writes.
 */
export const financingAdminUpsertOfferAPISchema = z
  .object({
    _id: objectIdSchema,
    /** Present → edit that offer. Absent → append a new one. */
    offerId: idSchema.optional(),
    /** Required for append mode; ignored on edit (lender is immutable). */
    lenderId: objectIdSchema.optional(),
    /**
     * Admin-writable decisions. `'accepted'` is excluded — only the
     * carrier (or a super-admin via the dedicated `acceptOffer` /
     * `markOutcome` flow) can flip an offer to `accepted`. Admin
     * back-fill can record `'interested'` to mirror an out-of-band
     * lender engagement on top of the dedicated express-interest
     * endpoint.
     */
    decision: z.enum(['pending', 'interested', 'offered', 'declined']),
    terms: financingOfferTermsSchema.optional(),
    declineReason: financingDeclineReasonSchema.optional(),
    declineNotes: z.string().trim().optional(),
    /**
     * Optional manual stamp for "the lender showed interest at this
     * time" — most callers leave this off and the service stamps
     * `interestedAt = now` automatically when transitioning out of
     * `pending`.
     */
    interestedAt: dateSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.decision === 'offered' && !data.terms) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms'],
        message: 'terms is required when decision is "offered"',
      });
    }
    if (data.decision === 'declined' && !data.declineReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['declineReason'],
        message: 'declineReason is required when decision is "declined"',
      });
    }
    if (!data.offerId && !data.lenderId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lenderId'],
        message: 'lenderId is required when appending a new offer',
      });
    }
  });
export type FinancingAdminUpsertOfferAPI = API<
  z.infer<typeof financingAdminUpsertOfferAPISchema>,
  FinancingApplication
>;

/**
 * `GET /financing/admin/applications` — admin list with the same
 * filters as the carrier list plus party-scoped filter. Mirrors the
 * `agreementGetManyAPISchema` shape so admin tables stay uniform.
 */
export const financingAdminGetAllAPISchema = FinancingApplicationSchema.model
  .pick({ status: true })
  .partial()
  .extend(paginationWithSearchSchema.shape)
  .extend({
    /** Filter to applications involving this party id (matches `tenants.id`). */
    partyId: objectIdSchema.optional(),
    /**
     * Filter to applications backing a specific bilateral invoice
     * (`FinancingApplication.invoice._id === invoiceId`). Used by
     * the shared `InvoiceDrawer` to detect existing factoring.
     */
    invoiceId: objectIdSchema.optional(),
  });
export type FinancingAdminGetAllAPI = API<
  z.infer<typeof financingAdminGetAllAPISchema>,
  FinancingApplication[]
>;

/**
 * `GET /financing/admin/applications/get-one` — single application by
 * id for the admin drawer. Returns the full doc (the carrier-side
 * list returns the same shape, but this dedicated read is admin-only
 * so super-admins can pull any record regardless of `tenants[]`).
 */
export const financingAdminGetOneAPISchema = z.object({
  _id: objectIdSchema,
});
export type FinancingAdminGetOneAPI = API<
  z.infer<typeof financingAdminGetOneAPISchema>,
  FinancingApplication
>;
